// @flow

import get from 'lodash/get';

import { createSelector } from 'reselect';

import {
  selectCartUUID as selectCartId,
  selectCartConfigurations,
  type CartConfiguration,
  type Process,
  type ProductType,
} from '@gandi/cart';

import type { FetchResponse } from '@gandi/resource/redux';

import { POST, PATCH } from 'gandi.redux-fetcher';

import { resellableCartConfKeys } from 'src/Cart/AccountChoice/modules/helpers';
import type { Dispatch, FullState, GetState } from 'src/types';

function getIdFromConflictResponse(response: FetchResponse<>): ?string {
  return get(response, 'data.errors.[0].location', '').split('=')[1];
}

// PATCHing cart configuration using `application/merge-patch+json` content-type
// Allows for a deep merge of a resource's contents.
// (update fields inside `resource.payload` without replacing `resource.payload`
// Necessary for `autorenew` handling inside cart configurations.
// See:
//   - https://tools.ietf.org/html/rfc7396
//   - sequoia.api.cart!230
//   - T70223
export const upsertConfiguration =
  (
    params: CartConfiguration,
    bypassConflict: boolean = true,
  ): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const { id, ...body } = params;

    const cartId = selectCartId(getState());

    const http = id ? PATCH : POST;
    const base = `/cart/carts/${cartId}/configurations`;
    const url = id ? `${base}/${id}` : base;

    return dispatch(
      http(url, body, {
        on20X: (response: FetchResponse<>) => response,
        // $FlowFixMe[incompatible-call]
        not20X: (response: FetchResponse<>) => {
          // HTTP 409 Conflict on POST.
          // A configuration already exsists for the given process/product_type.
          // Perform the same call with `PATCH` method on said configuration.
          if (response.status === 409 && !id && bypassConflict) {
            const conflictId = getIdFromConflictResponse(response);

            if (conflictId) {
              return dispatch(
                upsertConfiguration(
                  {
                    ...params,
                    id: Number(conflictId),
                  },
                  false,
                ),
              );
            }
          }

          const err = new Error('Unable to upsert cart configuration.');
          // $FlowFixMe
          err.response = response;
          return Promise.reject(err);
        },
        headers: {
          'content-type':
            http === PATCH
              ? 'application/merge-patch+json'
              : 'application/json',
        },
      }),
    );
  };

export const bulkConfiguration =
  (params: Array<any>): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const cartId = selectCartId(getState());

    const url = `/cart/carts/${cartId}/configurations/bulk`;

    return dispatch(
      POST(url, params, {
        on20X: (response: FetchResponse<>) => response,
        // $FlowFixMe[incompatible-call]
        not20X: (response: FetchResponse<>) => {
          const err = new Error('Unable to bulk cart configuration.');
          // $FlowFixMe
          err.response = response;
          return Promise.reject(err);
        },
        headers: {
          'content-type': 'application/json',
        },
      }),
    );
  };

export const unsetReselleeFromConfiguration =
  (): ((dispatch: Dispatch) => Promise<any>) => (dispatch: Dispatch) => {
    const bulkParams = resellableCartConfKeys.map(productKey => ({
      product_type: productKey.product_type,
      process: productKey.process,
      payload: {
        resellee_id: null,
        reseller_id: null,
      },
    }));

    return dispatch(bulkConfiguration(bulkParams));
  };

/** return a selector that will find the first configuration matching the given process and productType
 * @productType any productType (domain, certificate, paas...)
 * @process any process (create, restore, renew ...)
 *
 */
export const selectConfigurationFactory = ({
  productType,
  process,
}: {
  productType: ProductType,
  process: Process,
}): ((state: FullState) => ?CartConfiguration) =>
  createSelector(
    [selectCartConfigurations],
    (configurations: Array<CartConfiguration>) =>
      configurations.find(
        conf => conf.process === process && conf.product_type === productType,
      ),
  );
